import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import TitleReveal from '@/components/base/TitleReveal';

const promises = [
  { icon: 'ri-file-list-3-line', title: 'Estimate First', text: 'You see the diagnosis and price before any work begins.' },
  { icon: 'ri-phone-line', title: 'Personally Confirmed', text: 'Every request is reviewed and confirmed by a real person.' },
  { icon: 'ri-home-smile-line', title: 'Respect for Your Home', text: 'Protected floors and a tidy workspace on every visit.' },
  { icon: 'ri-chat-check-line', title: 'No Pressure', text: 'Clear options explained calmly. The decision stays yours.' },
];

gsap.registerPlugin(ScrollTrigger);

/**
 * Desktop: instead of a JS pin (which switches to position:fixed on the
 * scroll event and causes a one-frame "jolt"), the content is held with
 * native CSS position:sticky, handled by the browser compositor. A spacer
 * supplies the scroll distance. The ScrollTrigger only reads progress
 * (no pin) and drives the clip-path of an inner visual layer.
 * Tablet/mobile and reduced-motion users get the static image in normal flow.
 */
export default function ServicePhoto() {
  const sectionRef = useRef<HTMLElement>(null);
  const stickRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const stick = stickRef.current;
    const spacer = spacerRef.current;
    const text = textRef.current;
    const frame = frameRef.current;
    const visual = visualRef.current;
    const overlay = overlayRef.current;
    if (!section || !stick || !spacer || !text || !frame || !visual || !overlay) return undefined;

    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      // All values relative to the sticky holder (whose top == section top).
      const m = { l: 0, t: 0, w: 0, h: 0, vw: 0, vh: 0, p: 0 };
      const measure = () => {
        gsap.set(visual, { clearProps: 'left,top,width,height,clipPath' });
        const s = stick.getBoundingClientRect();
        const f = frame.getBoundingClientRect();
        m.l = f.left - s.left;
        m.t = f.top - s.top;
        m.w = f.width;
        m.h = f.height;
        m.vw = document.documentElement.clientWidth;
        m.vh = window.innerHeight;
        // Hold begins when the frame is centred (or top-aligned on short screens).
        m.p = m.h > m.vh ? m.t : Math.max(0, m.t + m.h / 2 - m.vh / 2);
        gsap.set(stick, { position: 'sticky', top: -m.p });
        gsap.set(spacer, { height: Math.round(m.vh * 1.8) });
        gsap.set(visual, {
          left: -m.l, top: m.p - m.t, width: m.vw, height: m.vh, borderRadius: 0,
        });
      };
      const startClip = () => {
        const top = Math.max(0, m.t - m.p);
        const bottom = Math.max(0, m.p + m.vh - (m.t + m.h));
        return `inset(${top}px ${m.vw - m.l - m.w}px ${bottom}px ${m.l}px round 12px)`;
      };
      measure();

      const revealEls = Array.from(overlay.querySelectorAll('[data-reveal]'));
      // Hide everything up-front so nothing flashes before the staggered reveal.
      gsap.set(overlay, { autoAlpha: 0 });
      gsap.set(revealEls, { autoAlpha: 0, y: 40 });

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: () => `top+=${Math.round(m.p)} top`,
          end: () => `+=${Math.round(window.innerHeight * 1.8)}`,
          scrub: 0.6,
          invalidateOnRefresh: true,
          refreshPriority: -1,
          onRefreshInit: measure,
        },
      });

      tl.to(text, { y: -48, opacity: 0, duration: 0.35, ease: 'power1.in' }, 0)
        .fromTo(
          visual,
          { clipPath: startClip },
          {
            clipPath: 'inset(0px 0px 0px 0px round 0px)', duration: 1, ease: 'power3.inOut', immediateRender: true,
          },
          0.05,
        )
        .fromTo(
          visual.querySelector('img'),
          { scale: 1.12 },
          { scale: 1, duration: 1, ease: 'power2.out', immediateRender: true },
          0.05,
        )
        .fromTo(overlay, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.25, ease: 'power1.out', immediateRender: false }, 0.85)
        .fromTo(
          revealEls,
          { autoAlpha: 0, y: 40 },
          { autoAlpha: 1, y: 0, duration: 0.35, stagger: 0.07, ease: 'power2.out', immediateRender: false },
          1.0,
        )
        .to({}, { duration: 0.35 });

      gsap.set(visual, { zIndex: 5, willChange: 'clip-path' });

      return () => {
        gsap.set([stick, spacer, visual, visual.querySelector('img'), overlay, ...revealEls], { clearProps: 'all' });
      };
    });

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    const img = visual.querySelector('img');
    img?.addEventListener('load', refresh);
    document.fonts?.ready.then(refresh).catch(() => undefined);

    return () => {
      window.removeEventListener('load', refresh);
      img?.removeEventListener('load', refresh);
      mm.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-background-50 pb-20 md:pb-32">
      <div ref={stickRef} className="relative">
        <Container>
          <div ref={textRef} className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16 mb-10 md:mb-14">
            <div>
              <Eyebrow label="Professional Service" />
              <TitleReveal className="mt-6 text-[34px] md:text-[48px] lg:text-[54px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">
                Good Repairs. Better Peace of Mind.
              </TitleReveal>
            </div>
            <p className="self-end max-w-md text-[15px] md:text-base leading-relaxed text-foreground-600">
              Every visit begins with careful diagnostics. We explain what we find, share a transparent estimate before any work starts, and leave your home as tidy as we found it.
            </p>
          </div>
          {/* Placeholder keeps the original frame size; the visual layer expands out of it. */}
          <div ref={frameRef} className="relative h-[300px] sm:h-[440px] lg:h-[640px] w-full">
            <div
              ref={visualRef}
              className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-xl"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-svcphoto.webp"
                width={1920}
                height={1000}
                loading="lazy"
                decoding="async"
                alt="MENDWELL technician diagnosing a washing machine at home"
                title="In-home washing machine repair"
                className="h-full w-full object-cover object-center"
              />
              <div ref={overlayRef} className="absolute inset-0 hidden lg:flex flex-col justify-end bg-gradient-to-t from-foreground-950/85 via-foreground-950/55 to-foreground-950/25 invisible opacity-0">
                <div className="mx-auto w-full max-w-7xl px-6 pb-20">
                  <p data-reveal className="invisible text-xs uppercase tracking-[0.25em] text-background-50/70">Our Promise</p>
                  <h3 data-reveal className="invisible mt-5 max-w-3xl text-[44px] xl:text-[56px] font-medium leading-[1.05] tracking-[-0.025em] text-background-50">
                    Repairs you can understand. A team you can trust.
                  </h3>
                  <div className="mt-14 grid grid-cols-4 gap-10 border-t border-background-50/20 pt-10">
                    {promises.map((p) => (
                      <div key={p.title} data-reveal className="invisible">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
                          <i className={`${p.icon} text-lg`} />
                        </div>
                        <h4 className="mt-5 text-lg font-medium text-background-50">{p.title}</h4>
                        <p className="mt-2 text-sm leading-relaxed text-background-50/70">{p.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
      {/* Desktop-only scroll distance for the sticky hold (0 height otherwise). */}
      <div ref={spacerRef} aria-hidden="true" />
    </section>
  );
}